// Building blocks shared by the answer widgets: card shell, segmented control,
// copy button, code block, callouts, gotcha chips and the error boundary.

import { Component, useState } from 'react'
import { paletteFor } from '../../../../shared/catalog.js'
import { copyText } from '../../../lib/clipboard.js'
import { highlightCode } from '../../../lib/json-highlight.jsx'
import { Icon } from '../../icons/Icon.jsx'
import { MentionChip } from '../../ui/MentionChip.jsx'
import { useWidgetContext } from './context.js'
import { CATEGORY_LABELS, SEVERITY_LABELS } from './text.js'
import w from './widgets.module.css'

export const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

export function Card({ label, title, titleMono = false, sub, right, children, flush = false }) {
  return (
    <section aria-label={label ?? (typeof title === 'string' ? title : undefined)} className={w.card}>
      {title || sub || right ? (
        <header className={w.head}>
          {title ? <span className={titleMono ? w.titleMono : w.title}>{title}</span> : null}
          {sub ? <span className={w.sub}>{sub}</span> : null}
          {right ? <div className={w.headRight}>{right}</div> : null}
        </header>
      ) : null}
      {flush ? children : <div className={w.body}>{children}</div>}
    </section>
  )
}

export function Seg({ label, options, value, onChange, disabled = false }) {
  return (
    <div className={w.seg} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** A button that stays pressed while it is on, in place of a checkbox. */
export function Toggle({ on, onChange, disabled = false, mono = false, label, children }) {
  return (
    <button
      type="button"
      className={`${w.pill} ${mono ? w.pillMono : ''}`}
      aria-pressed={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      {children}
    </button>
  )
}

/** Copies `text` (or the result of calling it) and confirms in place. */
export function CopyButton({ text, label = 'Copy', copiedLabel = 'Copied', primary = false, small = true }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    if (await copyText(typeof text === 'function' ? text() : text)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    }
  }
  return (
    <button
      type="button"
      className={`btn ${primary ? 'btn-dark' : 'btn-ghost'} ${small ? w.btnSm : ''}`}
      onClick={copy}
    >
      <Icon name={copied ? 'check' : 'copy'} size={13} strokeWidth={1.7} />
      {copied ? copiedLabel : label}
    </button>
  )
}

export function Code({ code, wrap = false }) {
  return <pre className={`${w.code} ${wrap ? w.codeWrap : ''}`}>{highlightCode(code)}</pre>
}

export function Watch({ label = 'Watch for:', children }) {
  return (
    <div className={w.watch}>
      <b>{label}</b> {children}
    </div>
  )
}

/** Text with `backtick` spans shown as code. */
export function InlineCode({ text }) {
  return String(text ?? '')
    .split(/(`[^`]+`)/g)
    .map((part, i) =>
      part.startsWith('`') && part.endsWith('`') && part.length > 2 ? (
        <code key={i} className={w.inlineCode}>
          {part.slice(1, -1)}
        </code>
      ) : (
        part
      ),
    )
}

export function SystemChip({ slug }) {
  const { systemsMap } = useWidgetContext()
  if (!slug) return null
  return <MentionChip system={systemsMap?.get(slug)} label={slug} variant="card" />
}

export function systemName(systemsMap, slug) {
  return systemsMap?.get(slug)?.name ?? slug ?? ''
}

/** CSS variables that colour a step by its system's palette. */
export function useSystemStyle() {
  const { systemsMap } = useWidgetContext()
  return (slug) => {
    const palette = paletteFor(systemsMap?.get(slug)?.palette)
    return {
      '--sys-bg': palette.flowBg,
      '--sys-border': palette.flowBorder,
      '--sys-text': palette.chipText,
      '--sys-solid': palette.tile,
    }
  }
}

/** Shown inside a card while its content is still streaming in. */
export function PendingLine({ children = 'Filling in…' }) {
  return <div className={w.pending}>{children}</div>
}

// ---- Gotchas, shared by the list and the cards they attach to ---------------

const SEVERITY_CLASS = { blocker: w.blocker, tip: w.tip }
const CHIP_CLASS = { blocker: w.gchipBlocker, tip: w.gchipTip }

export function GotchaBody({ item }) {
  return (
    <div className={w.gBody}>
      {item.why ? <div>{item.why}</div> : null}
      {item.fix ? (
        <div>
          <b>Do this:</b> {item.fix}
        </div>
      ) : null}
      {item.example ? <Code code={item.example} wrap /> : null}
    </div>
  )
}

export function GotchaCallout({ item, open, onToggle }) {
  return (
    <div className={`${w.gotcha} ${SEVERITY_CLASS[item.severity] ?? ''}`}>
      <button type="button" className={w.gHead} aria-expanded={open} onClick={onToggle}>
        <span className={w.sev}>{SEVERITY_LABELS[item.severity] ?? 'Note'}</span>
        <span className={w.gTitle}>{item.title}</span>
        {item.category ? <span className={w.gcat}>{CATEGORY_LABELS[item.category] ?? item.category}</span> : null}
        <span className={w.chev} aria-hidden="true">
          ›
        </span>
      </button>
      {open ? <GotchaBody item={item} /> : null}
    </div>
  )
}

/** Chips for gotchas attached to a card; clicking one opens it under the card. */
export function GotchaChips({ items, openId, onToggle }) {
  if (!items.length) return null
  return (
    <div className={w.rowTight}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`${w.gchip} ${CHIP_CLASS[item.severity] ?? ''}`}
          aria-expanded={openId === item.id}
          onClick={() => onToggle(openId === item.id ? null : item.id)}
        >
          <span aria-hidden="true">⚠</span>
          {item.title}
        </button>
      ))}
    </div>
  )
}

/** Chips plus the open callout, for a card that carries attached gotchas. */
export function AttachedGotchas({ items }) {
  const [openId, setOpenId] = useState(null)
  const list = asList(items)
  const open = list.find((g) => g.id === openId)
  if (!list.length) return null
  return (
    <>
      <GotchaChips items={list} openId={openId} onToggle={setOpenId} />
      {open ? <GotchaCallout item={open} open onToggle={() => setOpenId(null)} /> : null}
    </>
  )
}

// ---- Error boundary -----------------------------------------------------------

/** A widget that throws on odd input renders nothing instead of breaking the thread. */
export class WidgetBoundary extends Component {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error) {
    console.error('Widget failed to render', error)
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}
